/**
 * Offers & Promotions page content.
 *
 * Layer 1 (OFFERS) is the campaign list marketing edits: add, change, expire or
 * remove a campaign here and the page follows. Layer 2 (GENERAL_TERMS) is the
 * permanent policy text that applies to every campaign — no amounts, dates or
 * thresholds belong there.
 *
 * Text in [square brackets] is a placeholder still to be confirmed.
 * Clause and paragraph strings are trusted HTML (strong, links). Links use
 * "#id" for a place on this page and "/route" for a site page.
 */

export type OfferStatus = 'Active' | 'Upcoming' | 'Expired';

export interface OfferReward {
    pkg: string;
    discount: string;
    voucher: string;
}

export interface OfferCta {
    label: string;
    route: string;
    style: 'primary' | 'outline';
}

export interface OfferTermGroup {
    title: string;
    premium?: boolean;
    clauses: string[];
}

export interface Offer {
    id: string;
    name: string;
    description: string;
    segment: string;
    startDate: Date;
    endDate: Date;
    rewards: OfferReward[];
    voucherBrand: string;
    choiceNote: string;
    basisNote: string;
    premiumNote: string;
    creative: { src: string; alt: string };
    ctas: OfferCta[];
    termsHint: string;
    offerTerms: OfferTermGroup[];
}

export interface GeneralTerm {
    id: string;
    title: string;
    paragraphs: string[];
}

export interface Faq {
    q: string;
    a: string;
}

/** "5 November 2026" — for the terms text. */
export function formatOfferDate(d: Date): string {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** "05 Nov 2026" — for the card's validity bar. */
export function formatOfferDateShort(d: Date): string {
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Active between its dates (both inclusive), Upcoming before, Expired after. */
export function offerStatus(o: Offer, now = new Date()): OfferStatus {
    const end = new Date(o.endDate.getFullYear(), o.endDate.getMonth(), o.endDate.getDate(), 23, 59, 59, 999);
    if (now < o.startDate) {
        return 'Upcoming';
    }
    return now > end ? 'Expired' : 'Active';
}

/** Days the campaign runs for, counted from its start date. */
const CAMPAIGN_DAYS = 7;

/** The campaign period: from today to CAMPAIGN_DAYS later. */
function campaignPeriod(): { start: Date; end: Date } {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate() + CAMPAIGN_DAYS)
    };
}

export function buildOffers(): Offer[] {
    const period = campaignPeriod();
    const start = formatOfferDate(period.start);
    const end = formatOfferDate(period.end);

    return [
        {
            id: 'subscription-rewards',
            name: 'IndiaPicture Subscription Rewards',
            description: 'Subscribe to eligible IndiaPicture packages and enjoy additional value through a subscription discount or an equivalent Amazon shopping voucher.',
            segment: '[Eligible customer segment / region]',
            startDate: period.start,
            endDate: period.end,
            rewards: [
                { pkg: '₹10,000', discount: '₹1,000', voucher: '₹1,000' },
                { pkg: '₹15,000', discount: '₹1,500', voucher: '₹1,500' },
                { pkg: '₹25,000', discount: '₹2,500', voucher: '₹2,500' }
            ],
            voucherBrand: 'Amazon Gift',
            choiceNote: 'Pick <strong>either</strong> the discount <strong>or</strong> the Amazon Gift Voucher of equal value. The two cannot be combined.',
            basisNote: 'Values on package price, excl. GST',
            premiumNote: '<strong>Special iPhone reward:</strong> available only for select eligible subscriptions, subject to a qualifying business value, management approval, availability and the <a href="#offer-subscription-rewards-terms">offer terms</a>. It is not a guaranteed benefit of the ₹10,000, ₹15,000 or ₹25,000 packages.',
            creative: {
                src: 'assets/images/offers/subscription-rewards-v2.jpg',
                alt: 'IndiaPicture campaign banner: every visual you need on one platform, with millions of licensed images, 27+ AI models, edit and enhance tools and commercial licensing. Subscribe now and get rewarded: ₹1,000, ₹1,500 or ₹2,500 off (or an Amazon voucher) on ₹10,000, ₹15,000 and ₹25,000 subscriptions, plus a special iPhone 18 Pro Max offer on select eligible subscriptions.'
            },
            ctas: [
                { label: 'Enquire Now', route: '/contact', style: 'primary' },
                { label: 'Explore Subscription', route: '/subscription', style: 'outline' }
            ],
            termsHint: 'sections, including eligibility, voucher terms and the iPhone reward.',
            offerTerms: [
                {
                    title: 'Campaign period and eligibility',
                    clauses: [
                        `The campaign runs from ${start} to ${end}, both inclusive. Only qualifying purchases completed within this period are eligible.`,
                        'The campaign is open to [Eligible customer segments, e.g. new customers / all customers] located in [Eligible regions].',
                        'Qualifying packages are the ₹10,000, ₹15,000 and ₹25,000 IndiaPicture subscription packages at their published price, as shown in the table above. [Confirm exact package names and whether StudioIP or IP.ai packages are included.]',
                        'One benefit is available for each qualifying package.'
                    ]
                },
                {
                    title: 'Discount terms',
                    clauses: [
                        'The discount amount for each qualifying package is shown in the table above.',
                        'The discount is calculated on the eligible package value excluding applicable GST. It is applied to the package price before GST, and GST is charged on the discounted amount.',
                        'The discount is applied [on the tax invoice for the eligible package]. It has no cash value, cannot be exchanged for cash or credit and is not refundable separately.'
                    ]
                },
                {
                    title: 'Amazon Gift Voucher terms',
                    clauses: [
                        'You may choose either the stated discount or an Amazon Gift Voucher of equal value, not both. The discount and the voucher cannot be combined.',
                        'You state your choice [at the time of order / before the invoice is raised]. It cannot be changed once the discount has been applied to the invoice or the voucher has been issued.',
                        'The voucher is issued only after full payment for the qualifying package has been received and cleared and eligibility verification is complete. It is issued within [Voucher fulfilment timeline] of the later of payment confirmation and verification, by [delivery method].',
                        'Amazon Gift Vouchers are issued by a third party and are subject to Amazon’s applicable terms, redemption conditions and validity period. IndiaPicture does not guarantee acceptance, redemption or extension of a voucher beyond those terms.',
                        'Vouchers are non-transferable and cannot be exchanged for cash.'
                    ]
                },
                {
                    title: 'Special iPhone reward',
                    premium: true,
                    clauses: [
                        'A special iPhone reward is available <strong>only for select eligible subscriptions</strong> that meet a qualifying business value of [Qualifying business value] and the other conditions in this clause.',
                        '<strong>The iPhone is not automatically included with the ₹10,000, ₹15,000 or ₹25,000 packages and is not guaranteed to any customer.</strong> Purchasing one of those packages does not by itself qualify you for the iPhone.',
                        'Each iPhone reward is subject to management approval, stock availability, completion of verification and full payment. You are entitled to the reward only once IndiaPicture confirms the approval in writing.',
                        'The model and specification offered are [iPhone model and specification – to be confirmed]. If the stated model is unavailable, IndiaPicture may [offer an equivalent product of similar value / wait for stock – to be confirmed].',
                        'The reward is delivered within [iPhone fulfilment timeline] of approval and payment confirmation, to the address on your order.',
                        'Whether the iPhone reward is in addition to, or replaces, the discount or voucher on the same purchase is [to be confirmed by management].'
                    ]
                },
                {
                    title: 'Non-combination',
                    clauses: [
                        'This offer cannot be combined with any other IndiaPicture promotion, discount code, negotiated rate or partner offer unless IndiaPicture has specifically authorised this in writing.'
                    ]
                },
                {
                    title: 'Cancellations and refunds',
                    clauses: [
                        'If a qualifying package is cancelled, refunded, reversed or downgraded below the qualifying value, clause 10 of the <a href="#term-10">General Offers &amp; Promotions Terms</a> applies: a benefit not yet issued is withheld, and the value of a benefit already issued may be deducted or recovered in accordance with IndiaPicture’s approved policies.'
                    ]
                },
                {
                    title: 'Other applicable conditions',
                    clauses: [
                        'This offer is also subject to the <a href="#offer-terms">General Offers &amp; Promotions Terms and Conditions</a>, and to IndiaPicture’s <a href="/policy/disclaimer">Terms of Use</a>, <a href="/policy/disclaimer">subscription and service terms</a> and <a href="/policy/eula">image licensing terms</a>.',
                        'The offer does not change the licence, download allowance or permitted uses of any content.',
                        'Offer status, dates and any amendment are published on this page. The version published at the time of your qualifying purchase applies.',
                        'Questions about this offer: [Offers enquiry email] / [Offers enquiry phone], or your IndiaPicture sales contact.'
                    ]
                }
            ]
        }
    ];
}

export const GENERAL_TERMS: GeneralTerm[] = [
    {
        id: 'term-1',
        title: 'Scope and Applicability',
        paragraphs: [
            'These general terms govern all promotional offers made by IndiaPicture, including discounts, promotional pricing, gift vouchers, gifts, premium rewards and special subscription benefits ("offers").',
            'Each offer card on this page provides that offer’s own additional terms and conditions (“offer terms”), including its reward amounts, dates, eligibility and fulfilment details. An offer term applies only to that offer. It prevails over these general terms only to the extent of a difference that is clearly stated in the offer terms and permitted by applicable law. Anything the offer terms do not address is governed by these general terms.',
            'By claiming an offer you accept these general terms, that offer’s terms and IndiaPicture’s Terms of Use, subscription and service terms and image licensing terms.'
        ]
    },
    {
        id: 'term-2',
        title: 'Eligibility',
        paragraphs: [
            'An offer is available only to customers who meet the eligibility criteria stated for that offer. Criteria may include the subscription package or purchase value, the customer category (for example new or existing customers), location, the campaign period and any other condition stated in the offer terms.',
            'No offer is included automatically with an IndiaPicture subscription. A customer who does not meet the stated criteria is not entitled to the offer, even if they purchase a package of the stated value.',
            'Unless the offer terms state otherwise, an offer is limited to one claim per [customer account / billing entity] for each campaign.'
        ]
    },
    {
        id: 'term-3',
        title: 'Promotional Period and Qualifying Purchases',
        paragraphs: [
            'An offer is valid only during the promotional period stated for it and only while the offer remains available.',
            'To qualify, you must complete the required purchase or subscription, including placing the order and meeting the stated payment condition, within the promotional period. A purchase begun during the period but completed after it does not qualify unless the offer terms allow it.',
            'An offer that has ended is shown as expired or removed from this page and cannot be claimed.'
        ]
    },
    {
        id: 'term-4',
        title: 'Discounts and Promotional Pricing',
        paragraphs: [
            'A discount applies only to the packages and transactions named in the offer terms, which also state the discount amount, the basis of calculation and the way it is applied.',
            'Unless the offer terms state otherwise, a discount or other benefit is calculated on the eligible package value excluding applicable GST. GST is charged on the discounted value in accordance with applicable tax law.',
            'A discount has no cash value, cannot be exchanged for cash or credit and cannot be carried forward to another purchase.'
        ]
    },
    {
        id: 'term-5',
        title: 'Gift Vouchers',
        paragraphs: [
            'Where an offer provides a gift voucher as an alternative to a discount, you must choose one benefit at the time stated in the offer terms. The choice cannot be changed after the benefit has been applied or issued.',
            'A voucher is issued only after the eligibility checks in clause 11 are complete and payment has been confirmed in accordance with clause 7. The voucher issuer, value, delivery method, expected issue timeline and any issuer restriction are disclosed in the offer terms.',
            'Every voucher is subject to the issuer’s own terms, including validity period, redemption restrictions and eligible products. IndiaPicture does not guarantee acceptance or redemption of a voucher beyond the issuer’s terms and is not responsible for a voucher that expires unused after issue. Vouchers are non-transferable and cannot be exchanged for cash.'
        ]
    },
    {
        id: 'term-6',
        title: 'Gifts and Premium Rewards',
        paragraphs: [
            'Some campaigns may offer a gift or premium reward, such as an electronic device or other product, for selected qualifying purchases.',
            'A gift or premium reward is not a standard benefit of any subscription package. It may depend on a minimum qualifying business value, offer-specific eligibility, verification, stock availability and management approval, as stated in the offer terms. Until IndiaPicture confirms in writing that a reward has been approved for you, it is not guaranteed.',
            'Where a stated product is unavailable, IndiaPicture may [offer an equivalent product of similar value – to be confirmed]. Any tax liability arising from receiving a gift is [the customer’s responsibility – to be confirmed with finance].'
        ]
    },
    {
        id: 'term-7',
        title: 'Payment Confirmation and Benefit Fulfilment',
        paragraphs: [
            'A benefit is applied or issued only after IndiaPicture has received the payment required by the offer terms. A discount is normally reflected on the invoice; a voucher or gift is normally provided after full payment has been received and cleared.',
            'Fulfilment timelines for vouchers and gifts are stated in the offer terms and run from the later of payment confirmation and completion of eligibility verification. Vouchers and gift confirmations are sent to the contact details on your IndiaPicture account or order, so please keep them accurate.'
        ]
    },
    {
        id: 'term-8',
        title: 'Selection and Non-combination of Benefits',
        paragraphs: [
            'Where an offer provides alternative benefits, you may select only one of them for each qualifying purchase.',
            'An offer cannot be combined with any other IndiaPicture promotion, discount code, negotiated rate or partner offer unless the offer terms expressly permit it.'
        ]
    },
    {
        id: 'term-9',
        title: 'Exclusions and Restrictions',
        paragraphs: [
            'Unless the offer terms state otherwise, an offer does not apply to taxes, additional services, renewals, upgrades, add-ons, packages purchased before the promotional period or other transactions excluded in the offer terms. [Management to confirm the standard exclusion list, including negotiated rates and employee or partner purchases.]',
            'Please read the exclusions that apply to a specific offer before purchasing.'
        ]
    },
    {
        id: 'term-10',
        title: 'Cancellations, Refunds and Payment Reversals',
        paragraphs: [
            'Every benefit is conditional on the qualifying purchase remaining in force and paid for. Cancellation, refund and payment reversal requests are handled under IndiaPicture’s approved subscription and refund policies, which these terms do not change.',
            'Where a qualifying purchase is cancelled, refunded in whole or in part, reversed, charged back or downgraded below the qualifying value, IndiaPicture may, in accordance with its approved policies and applicable law, withhold a benefit not yet issued, deduct the value of a benefit already issued from any refund, or recover the value of a benefit already issued.'
        ]
    },
    {
        id: 'term-11',
        title: 'Verification and Prevention of Duplicate or Fraudulent Claims',
        paragraphs: [
            'IndiaPicture may verify your identity, company or billing entity, transaction details and eligibility before applying a benefit or issuing a reward, and may ask for reasonable supporting information.',
            'IndiaPicture may decline or withhold a benefit where, after review, it reasonably concludes that a claim is duplicated, made through multiple accounts for the same entity, based on inaccurate information or made in a way that circumvents the eligibility conditions. You will be told the reason and may ask for a review under clause 14. Suspected fraudulent activity is handled in accordance with applicable law and IndiaPicture’s approved policies.'
        ]
    },
    {
        id: 'term-12',
        title: 'Changes, Suspension and Withdrawal of Campaigns',
        paragraphs: [
            'IndiaPicture may introduce new campaigns at any time and may amend, suspend or withdraw an offer where this is permitted by applicable law and the offer terms. A change takes effect from the date it is published on this page, or a later date stated here.',
            'A change does not affect a benefit you have already validly earned by completing a qualifying purchase before the change takes effect, unless the change is required by law or you agree to it. The version of these terms published at the time of your qualifying purchase applies.'
        ]
    },
    {
        id: 'term-13',
        title: 'Relationship with Other IndiaPicture Terms',
        paragraphs: [
            'All purchases remain subject to IndiaPicture’s Terms of Use, subscription and service terms and image licensing terms.',
            'An offer changes the price or adds a benefit; it does not change the licence granted for stock images, AI-generated images or other content, the number of downloads or the permitted uses, unless the licensing terms expressly state otherwise. On any matter other than the offer itself, the Terms of Use and licensing terms prevail.'
        ]
    },
    {
        id: 'term-14',
        title: 'Customer Queries and Dispute Handling',
        paragraphs: [
            'Questions about an offer, including whether a purchase qualifies, can be raised with IndiaPicture at [Offers enquiry email] or [Offers enquiry phone].',
            'If you believe a benefit has been wrongly declined or withheld, you may ask for a review by writing to the same contact within [number] days of being notified. IndiaPicture will respond within [number] working days.',
            'These terms are governed by the laws of India. Nothing in them limits your rights under applicable consumer protection law. [Legal to confirm governing law and jurisdiction wording in line with the Terms of Use.]'
        ]
    }
];

export const FAQS: Faq[] = [
    {
        q: 'Are promotional rewards included with every IndiaPicture subscription?',
        a: 'No. Rewards are available only for the packages, customers and periods specified in an active campaign. Check the eligibility details on each offer.'
    },
    {
        q: 'Can I choose both a discount and an Amazon voucher?',
        a: 'No. Where a campaign offers a discount or an equivalent voucher, you can choose one of them, not both, unless the campaign explicitly says otherwise.'
    },
    {
        q: 'When will I receive my voucher?',
        a: 'Vouchers are issued after full payment has been received and your eligibility has been verified. The issue timeline is stated in each offer’s terms: open <a href="#offer-subscription-rewards-terms">View Offer Terms &amp; Conditions</a> in the current offer card. For the current campaign it has not been confirmed yet.'
    },
    {
        q: 'Is the iPhone offer available to everyone?',
        a: 'No. The iPhone reward is available only for select eligible subscriptions that meet a qualifying business value, subject to management approval and availability. It is not a benefit of the ₹10,000, ₹15,000 or ₹25,000 packages, and buying one of them does not by itself qualify you.'
    },
    {
        q: 'Can I use two promotions together?',
        a: 'No. Offers cannot be combined with other IndiaPicture promotions, discount codes or negotiated rates unless the offer’s terms specifically authorise it. Within one offer you also choose only one benefit.'
    },
    {
        q: 'How do I check whether an offer is still valid?',
        a: 'Each offer in the <a href="#current-offers">Current Offers</a> section shows its status (Active, Upcoming or Expired) and its validity dates. If you are unsure, contact us before purchasing.'
    },
    {
        q: 'Do promotional benefits change the commercial licence for images?',
        a: 'No. Your licence for stock images or other content is governed by IndiaPicture’s licensing terms, unless those terms expressly state otherwise.'
    },
    {
        q: 'Who can I contact about an offer?',
        a: 'Please use the official contact details published on this website: [Offers enquiry email] or [Offers enquiry phone]. You can also ask your IndiaPicture sales contact.'
    }
];
